import { Blueprint, BlueprintModule } from "../types";

// Pure helpers over a Make-format blueprint. The editor keeps the blueprint as
// its only source of truth and derives the canvas from it, so every edit here
// returns a new blueprint and never mutates the old one.

/** Where a new module goes. */
export type Placement =
    | { kind: 'start' }
    | { kind: 'after'; moduleId: number }
    | { kind: 'route'; routerId: number; routeIndex: number }
    | { kind: 'onerror'; moduleId: number };

export interface ModuleContext {
    /** Modules that run before this one on the way to it. */
    ancestors: number[];
    /** The module whose error route this module is in, if any. */
    errorOwner: number | null;
    /** The router whose route this module is in, and which route. */
    route: { routerId: number; routeIndex: number } | null;
    /** Index in its flow list, and the list's length. */
    index: number;
    listLength: number;
}

export const ROUTER = 'builtin:BasicRouter';

/** Calls fn for every module at any depth, with where it sits. */
export function walk(
    flow: BlueprintModule[],
    fn: (module: BlueprintModule, context: ModuleContext) => void,
    ancestors: number[] = [],
    errorOwner: number | null = null,
    route: ModuleContext['route'] = null,
): void {
    let previous = ancestors;
    flow.forEach((module, index) => {
        fn(module, { ancestors: previous, errorOwner, route, index, listLength: flow.length });
        const withSelf = [...previous, module.id];
        (module.routes || []).forEach((r, routeIndex) =>
            walk(r.flow || [], fn, withSelf, errorOwner, { routerId: module.id, routeIndex })
        );
        if (module.onerror && module.onerror.length > 0) {
            walk(module.onerror, fn, withSelf, module.id, null);
        }
        previous = withSelf;
    });
}

export function findModule(blueprint: Blueprint, id: number): BlueprintModule | undefined {
    let found: BlueprintModule | undefined;
    walk(blueprint.flow, (module) => {
        if (module.id === id) {
            found = module;
        }
    });

    return found;
}

export function moduleContext(blueprint: Blueprint, id: number): ModuleContext | undefined {
    let found: ModuleContext | undefined;
    walk(blueprint.flow, (module, context) => {
        if (module.id === id) {
            found = context;
        }
    });

    return found;
}

export function allModules(blueprint: Blueprint): BlueprintModule[] {
    const modules: BlueprintModule[] = [];
    walk(blueprint.flow, (module) => modules.push(module));

    return modules;
}

export function nextId(blueprint: Blueprint): number {
    return allModules(blueprint).reduce((max, module) => Math.max(max, module.id), 0) + 1;
}

/** Rebuilds every flow list, applying fn to each list (bottom-up). */
function mapLists(flow: BlueprintModule[], fn: (list: BlueprintModule[], owner: ListOwner) => BlueprintModule[], owner: ListOwner = { kind: 'main' }): BlueprintModule[] {
    const rebuilt = flow.map((module) => {
        let copy: BlueprintModule = module;
        if (module.routes) {
            copy = {
                ...copy,
                routes: module.routes.map((r, routeIndex) => ({ ...r, flow: mapLists(r.flow || [], fn, { kind: 'route', routerId: module.id, routeIndex }) })),
            };
        }
        if (module.onerror) {
            copy = { ...copy, onerror: mapLists(module.onerror, fn, { kind: 'onerror', moduleId: module.id }) };
        }

        return copy;
    });

    return fn(rebuilt, owner);
}

type ListOwner = { kind: 'main' } | { kind: 'route'; routerId: number; routeIndex: number } | { kind: 'onerror'; moduleId: number };

export function updateModule(blueprint: Blueprint, id: number, update: (module: BlueprintModule) => BlueprintModule): Blueprint {
    return {
        ...blueprint,
        flow: mapLists(blueprint.flow, (list) => list.map((module) => (module.id === id ? update(module) : module))),
    };
}

/**
 * Removes a module. Modules after it in the same list move up; its own routes
 * and error route go with it. An emptied error route is dropped.
 */
export function removeModule(blueprint: Blueprint, id: number): Blueprint {
    const flow = mapLists(blueprint.flow, (list) =>
        list
            .filter((module) => module.id !== id)
            .map((module) => {
                if (module.onerror && module.onerror.length === 0) {
                    const { onerror, ...rest } = module;

                    return rest as BlueprintModule;
                }

                return module;
            })
    );

    return { ...blueprint, flow };
}

export function insertModule(blueprint: Blueprint, placement: Placement, module: BlueprintModule): Blueprint {
    if (placement.kind === 'start') {
        return { ...blueprint, flow: [module, ...blueprint.flow] };
    }

    if (placement.kind === 'after') {
        return {
            ...blueprint,
            flow: mapLists(blueprint.flow, (list) => {
                const index = list.findIndex((m) => m.id === placement.moduleId);

                return index < 0 ? list : [...list.slice(0, index + 1), module, ...list.slice(index + 1)];
            }),
        };
    }

    if (placement.kind === 'route') {
        return updateModule(blueprint, placement.routerId, (router) => ({
            ...router,
            routes: (router.routes || []).map((r, i) => (i === placement.routeIndex ? { ...r, flow: [module, ...(r.flow || [])] } : r)),
        }));
    }

    return updateModule(blueprint, placement.moduleId, (owner) => ({ ...owner, onerror: [module, ...(owner.onerror || [])] }));
}

export function addRoute(blueprint: Blueprint, routerId: number): Blueprint {
    return updateModule(blueprint, routerId, (router) => ({ ...router, routes: [...(router.routes || []), { flow: [] }] }));
}

export function removeRoute(blueprint: Blueprint, routerId: number, routeIndex: number): Blueprint {
    return updateModule(blueprint, routerId, (router) => ({ ...router, routes: (router.routes || []).filter((_, i) => i !== routeIndex) }));
}

/** Module ids referenced as {{N.…}} in a mapper (mirrors the cabinet's check). */
export function referencedModules(value: any): number[] {
    const found = new Set<number>();
    const scan = (v: any) => {
        if (typeof v === 'string') {
            const blocks = v.match(/\{\{([\s\S]*?)\}\}/g) || [];
            blocks.forEach((block) => {
                const re = /(?<![\w.`"])(\d+)\.(?=[A-Za-z_`[])/g;
                let m: RegExpExecArray | null;
                while ((m = re.exec(block)) !== null) {
                    found.add(parseInt(m[1], 10));
                }
            });
        } else if (Array.isArray(v)) {
            v.forEach(scan);
        } else if (v && typeof v === 'object') {
            Object.values(v).forEach(scan);
        }
    };
    scan(value);

    return Array.from(found);
}

// -- Canvas -----------------------------------------------------------------

export const GAP_X = 260;
export const GAP_Y = 170;

export interface Position {
    x: number;
    y: number;
}

/** A "+" slot on the canvas: where clicking adds a module. */
export interface AddSlot {
    key: string;
    placement: Placement;
    /** The module the slot hangs off (for drawing the edge), if any. */
    from: number | null;
    position: Position;
}

export interface CanvasLayout {
    positions: Record<number, Position>;
    slots: AddSlot[];
}

/**
 * Default positions: each flow list runs left to right; a router's routes
 * stack downwards from it; an error route hangs below its module. Saved
 * designer coordinates win over the defaults.
 */
export function layout(blueprint: Blueprint, terminalKinds: (moduleKey: string) => 'router' | 'directive' | null): CanvasLayout {
    const positions: Record<number, Position> = {};
    const slots: AddSlot[] = [];

    const place = (list: BlueprintModule[], x: number, y: number, owner: ListOwner): number => {
        if (list.length === 0) {
            const placement: Placement = owner.kind === 'route'
                ? { kind: 'route', routerId: owner.routerId, routeIndex: owner.routeIndex }
                : owner.kind === 'onerror' ? { kind: 'onerror', moduleId: owner.moduleId } : { kind: 'start' };
            slots.push({ key: slotKey(placement), placement, from: owner.kind === 'route' ? owner.routerId : null, position: { x, y } });

            return 1;
        }

        let lanes = 1;
        list.forEach((module, index) => {
            const mx = x + index * GAP_X;
            positions[module.id] = module.metadata?.designer
                ? { x: module.metadata.designer.x, y: module.metadata.designer.y }
                : { x: mx, y };
            const base = positions[module.id];
            let used = 1;

            if (module.routes) {
                let routeY = base.y;
                let routeLanes = 0;
                module.routes.forEach((r, routeIndex) => {
                    const h = place(r.flow || [], base.x + GAP_X, routeY, { kind: 'route', routerId: module.id, routeIndex });
                    routeY += h * GAP_Y;
                    routeLanes += h;
                });
                used = Math.max(used, routeLanes);
            }
            if (module.onerror && module.onerror.length > 0) {
                used += place(module.onerror, base.x + GAP_X / 2, base.y + used * GAP_Y, { kind: 'onerror', moduleId: module.id });
            }
            lanes = Math.max(lanes, used);

            const isLast = index === list.length - 1;
            if (isLast && terminalKinds(module.module) === null) {
                const placement: Placement = { kind: 'after', moduleId: module.id };
                slots.push({ key: slotKey(placement), placement, from: module.id, position: { x: base.x + GAP_X, y: base.y } });
            }
        });

        return lanes;
    };

    place(blueprint.flow, 0, 0, { kind: 'main' });

    return { positions, slots };
}

export function slotKey(placement: Placement): string {
    switch (placement.kind) {
        case 'start':
            return 'add-start';
        case 'after':
            return 'add-after-' + placement.moduleId;
        case 'route':
            return 'add-route-' + placement.routerId + '-' + placement.routeIndex;
        default:
            return 'add-onerror-' + placement.moduleId;
    }
}

export interface CanvasEdge {
    id: string;
    source: number;
    target: number | string;
    kind: 'flow' | 'route' | 'error' | 'slot';
    /** The module whose filter sits on this edge (Make keeps it on the target). */
    filterOf: number | null;
    routeIndex?: number;
}

/** Edges between modules, and from modules to their "+" slots. */
export function edges(blueprint: Blueprint, slots: AddSlot[]): CanvasEdge[] {
    const result: CanvasEdge[] = [];

    const link = (list: BlueprintModule[]) => {
        list.forEach((module, index) => {
            const next = list[index + 1];
            if (next) {
                result.push({ id: `e-${module.id}-${next.id}`, source: module.id, target: next.id, kind: 'flow', filterOf: next.id });
            }
            (module.routes || []).forEach((r, routeIndex) => {
                const head = (r.flow || [])[0];
                if (head) {
                    result.push({ id: `r-${module.id}-${routeIndex}`, source: module.id, target: head.id, kind: 'route', filterOf: head.id, routeIndex });
                }
                link(r.flow || []);
            });
            if (module.onerror && module.onerror.length > 0) {
                result.push({ id: `x-${module.id}`, source: module.id, target: module.onerror[0].id, kind: 'error', filterOf: null });
                link(module.onerror);
            }
        });
    };
    link(blueprint.flow);

    slots.forEach((slot) => {
        if (slot.from !== null) {
            result.push({ id: `s-${slot.key}`, source: slot.from, target: slot.key, kind: 'slot', filterOf: null,
                routeIndex: slot.placement.kind === 'route' ? slot.placement.routeIndex : undefined });
        }
    });

    return result;
}

/** Writes the canvas position of a module into its designer metadata. */
export function moveModule(blueprint: Blueprint, id: number, position: Position): Blueprint {
    return updateModule(blueprint, id, (module) => ({
        ...module,
        metadata: { ...(module.metadata || {}), designer: { x: Math.round(position.x), y: Math.round(position.y) } },
    }));
}

/** A mapper path segment as IML wants it: `My field` needs backticks. */
export function pathSegment(key: string): string {
    return /^[A-Za-z_][A-Za-z0-9_]*$/.test(key) ? key : '`' + key + '`';
}
